import {
  getAllAreas,
  getAreasByBuildingId,
  getAreasByFloorId,
  getAvailableDeskAmenitiesForBuilding,
  getAvailableDeskAmenityDataByBuildingId,
  getFilteredAndSortedParkingLots,
  getFloorsByBuildingId,
  getLocationProfile,
  useAppDispatch,
  useAppSelector,
  withAsyncThunkErrorHandling,
} from '@lib/store';
import {uniq} from 'lodash';
import {useCallback, useEffect, useMemo} from 'react';
import {useTranslation} from 'react-i18next';

const useFloorOptions = ({buildingId}: {buildingId: string}) => {
  const {t} = useTranslation();
  const {disallowedLocationIds} = useAppSelector(getLocationProfile);

  const floors = useAppSelector((state) => getFloorsByBuildingId(state, buildingId));
  const allowedFloors = floors.filter((floor) => !disallowedLocationIds.includes(floor.id));

  const floorOptions = useMemo(
    () => [
      ...allowedFloors
        .filter((floor) => floor.capacity > 0)
        .map((floor) => ({
          value: floor.id,
          label: floor.name,
          icon: 'FloorStack',
        })),
      {
        value: null,
        label: t('workplace:NoPreferredFloor'),
        icon: 'QuestionMarkWithMarker',
      },
    ],
    [allowedFloors, t],
  );

  return floorOptions;
};

const useAreaOptions = () => {
  const {t} = useTranslation();
  const {disallowedLocationIds} = useAppSelector(getLocationProfile);

  const allAreas = useAppSelector(getAllAreas);

  const getAreaOptions = useCallback(
    ({buildingId, floorId}: {buildingId: string; floorId: string | null}) => {
      const potentialAreaOptions = floorId
        ? allAreas.filter((a) => a.floorId === floorId)
        : allAreas.filter((a) => a.buildingId === buildingId);

      const allowedAreas = potentialAreaOptions
        .filter((area) => !disallowedLocationIds.includes(area.id))
        .filter((area) => area.capacity > 0)
        .map((area) => ({
          value: area.id,
          label: area.name,
          icon: 'DeskNew',
        }));

      return allowedAreas;
    },
    [allAreas, disallowedLocationIds],
  );

  const getAreaOptionsForSelection = useCallback(
    ({buildingId, floorId}: {buildingId: string; floorId: string | null}) => {
      return [
        ...getAreaOptions({buildingId, floorId}),
        {
          value: null,
          label: t('workplace:NoPreferredArea'),
          icon: 'QuestionMarkWithMarker',
        },
      ];
    },
    [getAreaOptions, t],
  );

  return {getAreaOptions, getAreaOptionsForSelection};
};

const useDeskAmenityOptions = ({buildingId, floorId}: {buildingId: string; floorId: string | null}) => {
  const dispatch = useAppDispatch();
  const {t} = useTranslation();

  const {status: deskAmenitiesLoadingStatus} = useAppSelector(getAvailableDeskAmenityDataByBuildingId(buildingId));

  const areasByBuilding = useAppSelector((state) => getAreasByBuildingId(state, buildingId));
  const areasByFloor = useAppSelector((state) => (floorId ? getAreasByFloorId(state, floorId) : []));

  useEffect(() => {
    if (!buildingId) return;

    if (deskAmenitiesLoadingStatus === 'NotLoaded') {
      dispatch(withAsyncThunkErrorHandling(() => getAvailableDeskAmenitiesForBuilding({buildingId})));
    }
  }, [buildingId, deskAmenitiesLoadingStatus, dispatch]);

  const getDeskAmenities = useCallback(
    (areaId: string | null) => {
      const area = areasByBuilding.find((a) => a.id === areaId);
      if (area) {
        return area.deskAmenities;
      }

      // If no area is selected but a floor is, get the desk amenities for all areas on that floor
      if (floorId) {
        return uniq(areasByFloor.flatMap((a) => a.deskAmenities));
      }

      return [];
    },
    [areasByBuilding, areasByFloor, floorId],
  );

  // If an area was selected, get the desk amenities for that area, otherwise get the desk amenities for the building
  const getDeskAmenityOptionsForSelection = useCallback(
    (areaId: string | null) => {
      return getDeskAmenities(areaId).map((deskAmenity) => {
        return {
          value: deskAmenity,
          label: t(`deskAmenities:deskAmenity`, {context: deskAmenity}),
          disabled: false,
        };
      });
    },
    [getDeskAmenities, t],
  );

  // Get desk amenities that are available in the FLOOR but not in the selected area
  const getDeskAmenitiesOutsideOfArea = useCallback(
    (areaId: string | null) => {
      const amenitiesFromArea = getDeskAmenities(areaId);
      if (!floorId) return [];

      return uniq(areasByFloor.flatMap((a) => a.deskAmenities)).filter(
        (amenity) => !amenitiesFromArea.includes(amenity),
      );
    },
    [areasByFloor, floorId, getDeskAmenities],
  );

  const getDeskAmenityOptionsForSelectionOutsideOfArea = useCallback(
    (areaId: string | null) => {
      return getDeskAmenitiesOutsideOfArea(areaId).map((deskAmenity) => ({
        value: deskAmenity,
        label: t(`deskAmenities:deskAmenity`, {context: deskAmenity}),
        disabled: true,
      }));
    },
    [getDeskAmenitiesOutsideOfArea, t],
  );

  return {
    isLoading: deskAmenitiesLoadingStatus === 'Loading',
    getDeskAmenities,
    getDeskAmenityOptionsForSelection,
    getDeskAmenitiesOutsideOfArea,
    getDeskAmenityOptionsForSelectionOutsideOfArea,
  };
};

const useParkingLotOptions = ({buildingId}: {buildingId: string}) => {
  const {t} = useTranslation();

  const parkingLots = useAppSelector((state) => getFilteredAndSortedParkingLots(state, buildingId));
  const parkingLotOptions = useMemo(
    () => [
      ...parkingLots.map((lot) => ({
        value: lot.id,
        label: lot.name,
      })),
      {
        value: null,
        label: t('parking:ManualSelection'),
      },
    ],
    [parkingLots, t],
  );

  return parkingLotOptions;
};

const useParkingZoneOptions = (buildingId: string) => {
  const {t} = useTranslation();

  const allLots = useAppSelector((state) => getFilteredAndSortedParkingLots(state, buildingId));

  const getParkingZones = useCallback(
    (parkingLotId?: string | null) => {
      const parkingZones = allLots.find((pl) => pl.id === parkingLotId)?.parkingZones ?? [];

      const parkingZoneOptionsMapped = parkingZones
        .filter((parkingZone) => parkingZone.capacity > 0)
        .filter((zone) => zone.parentId === parkingLotId)
        .map((zone) => ({
          value: zone.id,
          label: zone.name,
        }));

      return parkingZoneOptionsMapped;
    },
    [allLots],
  );

  const getParkingZonesOptionsForSelection = useCallback(
    (parkingLotId?: string | null) => {
      const parkingZoneOptionsMapped = getParkingZones(parkingLotId);

      const isParkingZoneSelectionMandatory = !!parkingLotId && parkingZoneOptionsMapped.length > 0;

      return [
        ...parkingZoneOptionsMapped,
        // If a parking lot is selected and it has zones, then picking a zone is mandatory (so no null option)
        ...(!isParkingZoneSelectionMandatory ? [{value: null, label: t('parking:ManualSelection')}] : []),
      ];
    },
    [getParkingZones, t],
  );

  return {getParkingZones, getParkingZonesOptionsForSelection};
};

export {useFloorOptions, useAreaOptions, useDeskAmenityOptions, useParkingLotOptions, useParkingZoneOptions};
